/**
 * Afriqahome Auth API
 * 
 * Autentisering och användarhantering.
 */

import { authApi, apiGet, apiUpsert, apiPatch } from './client';
import { DbProfile, User, AuthSession, dbProfileToUser } from './types';
// BUG-075: refreshAccessToken() rotates through the Supabase client so that
// client stays the single owner of the session. supabaseClient imports nothing
// from lib/api, so this does not create a cycle.
import { supabase } from '../supabaseClient';

/**
 * localStorage keys for auth session storage.
 *
 * KNOWN LIMITATION: These keys are static (not user-specific).
 * If two different users test in the SAME browser (different tabs),
 * their sessions will overwrite each other. This is expected behavior
 * since browsers isolate localStorage per origin, not per user.
 *
 * Cross-device isolation: Different devices have separate localStorage,
 * so this does NOT affect users on different devices/browsers.
 */
const STORAGE_KEYS = {
  accessToken: 'afriqahome_access_token',
  refreshToken: 'afriqahome_refresh_token',
  user: 'afriqahome_user',
  expiresAt: 'afriqahome_expires_at'
};

/**
 * Registrera ny användare
 */
export async function signUp(
  email: string, 
  password: string, 
  fullName: string,
  role: 'buyer' | 'agent' = 'buyer'
): Promise<{ user: User | null; error: string | null }> {
  // 1. Skapa auth user
  const { data: authData, error: authError } = await authApi.signUp(email, password);
  
  if (authError || !authData?.user) {
    return { user: null, error: authError?.message || 'Signup failed' };
  }

  // 2. Skapa/uppdatera profil using UPSERT to avoid duplicate key errors
  // NOTE: Supabase may have a trigger that creates profile with role='buyer' automatically
  const profileData = {
    id: authData.user.id,
    email: email,
    full_name: fullName,
    role: role,
    tier: role === 'agent' ? 'ignite' : null,
    is_verified: false,
    tos_accepted_at: new Date().toISOString()
  };

  // Small delay to let any Supabase trigger run first
  await new Promise(resolve => setTimeout(resolve, 200));

  // Use UPSERT to handle both new and existing profiles
  const { error: profileError } = await apiUpsert<DbProfile[]>(
    'profiles',
    profileData,
    'id',
    authData.access_token || undefined
  );

  if (profileError) {
    console.warn('⚠️ Profile upsert warning:', profileError.message);
  }

  // 3. Om email confirmation är av, logga in direkt
  if (authData.access_token) {
    saveSession({
      accessToken: authData.access_token,
      refreshToken: authData.refresh_token,
      user: {
        id: authData.user.id,
        email: email,
        role: role,
        name: fullName
      },
      expiresAt: Date.now() + (authData.expires_in * 1000)
    });

    return {
      user: {
        id: authData.user.id,
        email: email,
        role: role,
        name: fullName
      },
      error: null
    };
  }

  // Email confirmation är på
  return { 
    user: null, 
    error: 'Please check your email to confirm your account' 
  };
}

/**
 * Logga in användare
 */
export async function signIn(
  email: string, 
  password: string
): Promise<{ user: User | null; error: string | null }> {
  // 1. Auth login
  const { data: authData, error: authError } = await authApi.signIn(email, password);
  
  if (authError || !authData?.access_token) {
    return { user: null, error: authError?.message || 'Invalid credentials' };
  }

  // 2. Hämta profil
  const { data: profiles, error: profileError } = await apiGet<DbProfile[]>(
    'profiles',
    `id=eq.${authData.user.id}&select=*`,
    authData.access_token
  );

  const profile = profiles?.[0];

  // FIX: Log warning and try to create profile if missing using upsert
  if (!profile) {
    console.warn('⚠️ Profile not found for user:', authData.user.id);

    // Try to create a basic profile to prevent future issues using upsert
    const { error: createError } = await apiUpsert<DbProfile[]>('profiles', {
      id: authData.user.id,
      email: email,
      full_name: email.split('@')[0],
      role: 'buyer', // Default to buyer if no profile exists
      is_verified: false
    }, 'id', authData.access_token);

    if (createError) {
      console.error('Failed to create missing profile:', createError);
    }
  }

  const user: User = profile
    ? dbProfileToUser(profile, email)
    : {
        id: authData.user.id,
        email: email,
        role: 'buyer', // Fallback - but profile should exist now
        name: email.split('@')[0]
      };

  // 3. Spara session
  saveSession({
    accessToken: authData.access_token,
    refreshToken: authData.refresh_token,
    user: user,
    expiresAt: Date.now() + (authData.expires_in * 1000)
  });

  return { user, error: null };
}

/**
 * Logga ut
 */
export function signOut(): void {
  authApi.signOut();
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.expiresAt);
}

/**
 * Hämta aktuell session (från localStorage)
 */
export function getSession(): AuthSession | null {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    const userStr = localStorage.getItem(STORAGE_KEYS.user);
    const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);

    if (!accessToken || !userStr) {
      return null;
    }

    const user = JSON.parse(userStr) as User;
    
    return {
      accessToken,
      refreshToken: refreshToken || '',
      user,
      expiresAt: expiresAt ? parseInt(expiresAt) : 0
    };
  } catch {
    return null;
  }
}

/**
 * Kolla om användaren är inloggad
 */
export function isAuthenticated(): boolean {
  const session = getSession();
  if (!session) return false;

  // Check if token is expired (with 5 min buffer)
  // Note: Actual token refresh happens in getValidAccessToken()
  const bufferTime = 5 * 60 * 1000; // 5 minutes
  if (session.expiresAt && (session.expiresAt - bufferTime) < Date.now()) {
    // Token is expired or about to expire
    // Check if we have a refresh token - if so, consider still authenticated
    // The actual refresh will happen on the next API call
    const refreshToken = localStorage.getItem('afriqahome_refresh_token');
    if (refreshToken) {
      return true; // Let getValidAccessToken handle the refresh
    }
    signOut();
    return false;
  }

  return true;
}

/**
 * Hämta aktuell användare
 */
export function getCurrentUser(): User | null {
  const session = getSession();
  return session?.user || null;
}

/**
 * Hämta access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

/**
 * BUG-075: write a rotated session into the afriqahome_* mirror.
 * Single place so both refresh regimes below store the same shape.
 */
function persistRefreshedTokens(
  accessToken: string,
  refreshToken: string | null | undefined,
  expiresIn: number | null | undefined
): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
  }
  localStorage.setItem(
    STORAGE_KEYS.expiresAt,
    (Date.now() + (expiresIn || 3600) * 1000).toString()
  );
}

/**
 * BUG-075: drop the whole custom session, tokens included.
 * `signOut()` deliberately leaves the tokens in place for its own callers; this
 * is for the case where the refresh token is proven dead and leaving a stale
 * access token behind would make getValidAccessToken() hand it out unchecked.
 */
function clearStoredSession(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.expiresAt);
}

/**
 * Refresh access token using refresh token
 * Returns the new access token or null if refresh fails
 */
export async function refreshAccessToken(): Promise<string | null> {
  // BUG-075 (PR #625 review, P1): refresh through supabase-js, never through a
  // hand-written call to /auth/v1/token.
  //
  // Supabase refresh tokens are single-use. The previous implementation posted
  // the stored refresh token to GoTrue directly and wrote the rotated pair into
  // the afriqahome_* keys only — supabase-js was never told. Its own stored
  // session kept the now-consumed refresh token, so its next scheduled refresh
  // (roughly 90s before expiry) was rejected; it dropped the session and
  // emitted SIGNED_OUT, whose handler then cleared the custom session we had
  // just refreshed. The result was a deterministic sign-out shortly after
  // apparently rescuing the session — and, once the profile form started
  // calling getValidAccessToken(), one that could land mid-form. That is this
  // item's own failure, relocated rather than removed.
  //
  // Letting the client own rotation makes it the single source of truth: it
  // emits TOKEN_REFRESHED, and AuthContext's handler mirrors the rotated tokens
  // into the afriqahome_* keys. One store rotates, one direction of sync.
  // There are two disjoint session regimes in this app, and the correct refresh
  // differs between them:
  //
  //   1. supabase-js owns the session — OAuth, supabase.auth.signUp() and the
  //      email-confirmation link (detectSessionInUrl). The client auto-refreshes
  //      hourly, so refreshing behind its back is what caused the P1.
  //   2. Only the afriqahome_* keys exist — the legacy password login, which
  //      posts to /auth/v1/token?grant_type=password (lib/api/client.ts) and
  //      never hands the session to supabase-js. There is no client session to
  //      desynchronise, and nothing else refreshes these users at all.
  //
  // So: ask the client first, and fall back only when it genuinely has nothing.
  try {
    const { data: { session: clientSession } } = await supabase.auth.getSession();

    if (clientSession) {
      // Regime 1 — the client owns rotation. It emits TOKEN_REFRESHED and
      // AuthContext mirrors the result; we mirror here too rather than depend
      // on the event having been delivered before this returns, since the
      // caller uses the token immediately. Same values from the same source, so
      // the two writers agree by construction.
      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        // Do NOT sign out here. supabase-js already separates the cases: a real
        // auth rejection drops its session and emits SIGNED_OUT (that handler
        // owns the cleanup), while a transient network failure leaves the
        // session intact so a later attempt can succeed. The old code signed
        // people out over a dropped connection.
        console.error('Token refresh failed:', error?.message ?? 'no session returned');
        return null;
      }

      persistRefreshedTokens(
        data.session.access_token,
        data.session.refresh_token,
        data.session.expires_in
      );
      return data.session.access_token;
    }

    // Regime 2 — no client session, so this refresh token is exclusively ours
    // and the single-use conflict cannot arise. Nothing to keep in sync either:
    // adopting the result into supabase-js via setSession() would fire a
    // SIGNED_IN through AuthContext's heavy handler (profile fetch, promo
    // activation, navigation) mid-form, which is not a trade this item should
    // make. Left as a follow-up; recorded on the Notion item.
    const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!refreshToken) {
      return null;
    }

    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      console.error('Token refresh failed:', response.status);
      // Distinguish a dead session from a bad connection. A 4xx means this
      // refresh token is spent or revoked and no SIGNED_OUT will arrive to tidy
      // up (supabase-js has no session here), so clear the custom session
      // ourselves. A 5xx or network failure is transient — keep the session so
      // a later attempt can recover.
      //
      // The previous code called signOut() on every non-OK response, which
      // removed only `user` and `expiresAt` and left the tokens behind. With
      // expiresAt gone, getValidAccessToken()'s `if (expirationTime && ...)`
      // guard was false, so it then returned the dead access token forever,
      // with no expiry check and no further refresh attempt.
      if (response.status >= 400 && response.status < 500) {
        clearStoredSession();
      }
      return null;
    }

    const data = await response.json();

    if (data.access_token) {
      persistRefreshedTokens(data.access_token, data.refresh_token, data.expires_in);
      return data.access_token;
    }

    return null;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
}

/**
 * Get a valid access token, refreshing if necessary
 * This should be used instead of getAccessToken() for API calls
 */
export async function getValidAccessToken(): Promise<string | null> {
  const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
  const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);

  if (!accessToken) {
    return null;
  }

  // Check if token is expired or about to expire (5 minute buffer)
  const expirationTime = expiresAt ? parseInt(expiresAt) : 0;
  const bufferTime = 5 * 60 * 1000; // 5 minutes

  if (expirationTime && (Date.now() + bufferTime) >= expirationTime) {
    // Token is expired or about to expire, try to refresh
    const newToken = await refreshAccessToken();
    return newToken;
  }

  return accessToken;
}

/**
 * Uppdatera användarprofil
 */
export async function updateProfile(
  userId: string,
  updates: Partial<DbProfile>
): Promise<{ user: User | null; error: string | null }> {
  const accessToken = getAccessToken();
  
  if (!accessToken) {
    return { user: null, error: 'Not authenticated' };
  }

  const { data, error } = await apiPatch<DbProfile[]>(
    'profiles',
    `id=eq.${userId}`,
    updates,
    accessToken
  );

  if (error || !data || data.length === 0) {
    return { user: null, error: error?.message || 'Failed to update profile' };
  }

  const user = dbProfileToUser(data[0]);
  
  // Uppdatera cached user
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));

  return { user, error: null };
}

/**
 * Spara session till localStorage
 */
function saveSession(session: AuthSession): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, session.accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, session.refreshToken);
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(session.user));
  localStorage.setItem(STORAGE_KEYS.expiresAt, session.expiresAt.toString());
}